import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { smsAPI } from '../../services/api';
import { formatIndianCurrency } from '../../utils/numberFormat';

const ModernSidebarV2 = ({ navigation, sidebarOpen, setSidebarOpen, user, logout }) => {
  const location = useLocation();
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [balanceData, setBalanceData] = useState(null);
  const [balanceRefreshing, setBalanceRefreshing] = useState(false);

  const handleMenuClick = () => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const isSubmenuActive = (children) => {
    return children?.some(child => isActive(child.href));
  };

  const toggleSubmenu = (menuKey) => {
    setOpenSubmenu(openSubmenu === menuKey ? null : menuKey);
  };

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const response = await smsAPI.getBalanceWithSMS();
        const balanceInfo = response.data?.data || response.data;
        setBalanceData(balanceInfo);
      } catch (error) {
        console.error('Failed to fetch balance:', error);
      }
    };
    
    if (user?.role === 'user' || user?.role === 'USER') {
      fetchBalance();
    }
  }, [user]);

  // Click handler to refresh balance from Redis + DB
  const handleBalanceClick = async () => {
    if (balanceRefreshing) return;
    setBalanceRefreshing(true);
    try {
      const response = await smsAPI.refreshBalance();
      const balanceInfo = response.data?.data || response.data;
      setBalanceData(balanceInfo);
    } catch (error) {
      console.error('Failed to refresh balance:', error);
    } finally {
      setBalanceRefreshing(false);
    }
  };

  useEffect(() => {
    navigation.forEach((item) => {
      if (item.children && isSubmenuActive(item.children)) {
        setOpenSubmenu(item.menuKey);
      }
    });
  }, [location.pathname]);

  const getIcon = (iconName) => {
    const IconComponent = Icons[iconName];
    return IconComponent ? <IconComponent className="w-[18px] h-[18px]" /> : <Icons.Activity className="w-[18px] h-[18px]" />;
  };

  return (
    <>
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-40 lg:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div
        className={`fixed left-0 bottom-0 z-40 bg-white transition-transform duration-300 lg:translate-x-0 border-r border-gray-200 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{
          top: '60px',
          width: sidebarOpen && window.innerWidth < 1024 ? '75%' : '240px',
        }}
      >
        <div className="flex flex-col h-full">
          <div className="lg:hidden flex justify-end p-3 border-b border-gray-100">
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <Icons.X className="w-4 h-4" />
            </button>
          </div>

          <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
            {navigation.map((item) => {
              if (item.children) {
                const isOpen = openSubmenu === item.menuKey;
                const hasActiveChild = isSubmenuActive(item.children);
                
                return (
                  <div key={item.name}>
                    <button
                      onClick={() => toggleSubmenu(item.menuKey)}
                      className={`w-full group flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        hasActiveChild
                          ? 'bg-indigo-50 text-indigo-700'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <div className={hasActiveChild ? 'text-indigo-600' : 'text-gray-500 group-hover:text-gray-700'}>
                        {getIcon(item.icon)}
                      </div>
                      <span className="flex-1 text-left">{item.name}</span>
                      <Icons.ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${
                        isOpen ? 'rotate-180' : ''
                      }`} />
                    </button>

                    {isOpen && (
                      <div className="mt-0.5 space-y-0.5 ml-2">
                        {item.children.map((child) => {
                          const childActive = isActive(child.href);
                          return (
                            <Link
                              key={child.name}
                              to={child.href}
                              onClick={handleMenuClick}
                              className={`group flex items-center gap-2.5 pl-7 pr-3 py-2 rounded-lg text-sm font-medium transition-all ${
                                childActive
                                  ? 'bg-indigo-50 text-indigo-700'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                              }`}
                            >
                              <div className={childActive ? 'text-indigo-600' : 'text-gray-400 group-hover:text-gray-600'}>
                                {getIcon(child.icon)}
                              </div>
                              <span>{child.name}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              const active = isActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={handleMenuClick}
                  className={`group flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    active
                      ? 'bg-indigo-50 text-indigo-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <div className={active ? 'text-indigo-600' : 'text-gray-500 group-hover:text-gray-700'}>
                    {getIcon(item.icon)}
                  </div>
                  <span>{item.name}</span>
                  {item.badge && (
                    <span className="ml-auto px-1.5 py-0.5 text-xs font-semibold bg-red-500 text-white rounded-full">
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="p-3 border-t border-gray-200">
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-3 border border-gray-200 shadow-sm">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="relative flex-shrink-0">
                  <div className="w-9 h-9 bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-sm">
                    {user?.full_name?.[0] || user?.username?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-white rounded-full" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {user?.full_name || user?.username}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                </div>
              </div>

              {(user?.balance !== undefined || balanceData) && (
                <div
                  className="mb-3 p-2 bg-white rounded-lg border border-gray-200 cursor-pointer hover:bg-indigo-50 hover:border-indigo-200 transition-all duration-200"
                  onClick={handleBalanceClick}
                  title="Click to refresh balance"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-600 flex items-center gap-1">
                      Balance
                      <Icons.RefreshCw className={`w-3 h-3 ${balanceRefreshing ? 'animate-spin' : ''}`} style={{ opacity: 0.5 }} />
                    </span>
                    <span className="text-sm font-bold text-green-600">
                      {balanceRefreshing ? '...' : formatIndianCurrency(balanceData?.available_balance || user.balance || 0)}
                    </span>
                  </div>
                  {balanceData?.sms_capacity > 0 && (
                    <div className="flex items-center justify-between pt-1 border-t border-gray-100">
                      <span className="text-xs font-medium text-gray-600">SMS</span>
                      <span className="text-sm font-bold text-indigo-600">
                        {balanceRefreshing ? '...' : balanceData.sms_capacity.toLocaleString()}
                      </span>
                    </div>
                  )}
                  
                </div>
              )}

              <button
                onClick={logout}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-white hover:bg-red-50 text-red-600 hover:text-red-700 rounded-lg text-sm font-medium transition-all border border-red-200 hover:border-red-300"
              >
                <Icons.LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ModernSidebarV2;
